import React, { useEffect, useState } from "react";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min.css";
import { Col, Form, Row } from "react-bootstrap";
import { FaEye, FaStar, FaUserEdit } from "react-icons/fa";
import { FaCircleXmark } from "react-icons/fa6";
import { MdDeleteForever } from "react-icons/md";
import {
  AddButton,
  Button,
  Input,
  SubTitle,
} from "../../Components/CustomComponents";
import DeleteModal from "../../Components/Modals/DeleteModal";
import ModalContainer from "../../Components/ModalContainer";
import Table from "../../Components/Table/Table";
import Toast from "../../Hooks/useToast";
import useShopController from "../../controllers/ShopControllers/useShopController";
import modalStyles from "../../Components/Modals/styles/editModal.module.scss";
import styles from "./styles/shop_management.module.scss";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function emptyForm() {
  return {
    name: "",
    email: "",
    phone: "",
    social_facebook: "",
    social_instagram: "",
    social_tiktok: "",
    longitude: "",
    latitude: "",
    address: "",
    about: "",
    opening_hours: [],
  };
}

function rowToForm(row) {
  const sl = row.social_links || {};
  const hours = Array.isArray(row.opening_hours) ? row.opening_hours : [];
  return {
    name: row.name ?? "",
    email: row.email ?? "",
    phone: row.phone ?? "",
    social_facebook: sl.facebook ?? "",
    social_instagram: sl.instagram ?? "",
    social_tiktok: sl.tiktok ?? "",
    longitude: row.longitude != null ? String(row.longitude) : "",
    latitude: row.latitude != null ? String(row.latitude) : "",
    address: row.address ?? "",
    about: row.about ?? "",
    opening_hours: hours.map((h) => ({
      day: h.day ?? "Mon",
      time_from: h.time_from ?? "",
      time_to: h.time_to ?? "",
    })),
  };
}

function formToPayload(form) {
  const hours = (form.opening_hours || [])
    .filter((h) => h.day && (h.time_from || h.time_to))
    .map((h) => ({
      day: h.day,
      time_from: h.time_from?.trim() || "",
      time_to: h.time_to?.trim() || "",
    }));

  return {
    name: form.name.trim(),
    email: form.email.trim(),
    phone: form.phone.trim(),
    social_links: Object.fromEntries(
      [
        ["facebook", form.social_facebook.trim()],
        ["instagram", form.social_instagram.trim()],
        ["tiktok", form.social_tiktok.trim()],
      ].filter(([, v]) => v !== "")
    ),
    longitude: form.longitude.trim(),
    latitude: form.latitude.trim(),
    address: form.address.trim(),
    about: form.about.trim(),
    opening_hours: hours,
  };
}

function formatRating(row) {
  const r =
    row.rating ??
    row.avg_rating ??
    row.star_rating ??
    row.shop_rating;
  if (r == null || r === "") return "—";
  return String(r);
}

function formatServices(row) {
  const raw = row.services ?? row.Services;
  if (raw == null) return null;
  if (Array.isArray(raw)) {
    if (raw.length === 0) return "—";
    return raw.map((item, i) => {
      if (item == null) return null;
      if (typeof item === "string") return item;
      return (
        item.name ??
        item.title ??
        item.service_name ??
        JSON.stringify(item)
      );
    });
  }
  return String(raw);
}

function formatTimeDisplay(t) {
  if (t == null || t === "") return "—";
  const s = String(t).trim();
  if (/^\d{1,2}:\d{2}:\d{2}$/.test(s)) return s.slice(0, 5);
  if (/^\d{1,2}:\d{2}$/.test(s)) return s;
  return s;
}

function getOpeningDayLabel(h) {
  if (h == null) return "—";
  const candidates = [
    h.day,
    h.day_name,
    h.weekday,
    h.day_of_week,
    h.week_day,
  ];
  for (const c of candidates) {
    if (c != null && String(c).trim() !== "") return String(c).trim();
  }
  return "—";
}

function hrefToWeb(url) {
  const u = String(url).trim();
  if (!u) return "#";
  if (/^https?:\/\//i.test(u)) return u;
  return `https://${u}`;
}

function ShopViewBody({ row, onClose }) {
  if (!row) return null;
  const sl = row.social_links || {};
  const socialEntries = Object.entries(sl).filter(
    ([, v]) => v != null && String(v).trim() !== ""
  );
  const hours = Array.isArray(row.opening_hours) ? row.opening_hours : [];
  const servicesList = formatServices(row);
  const ratingStr = formatRating(row);
  const statusNum = row.status;
  const statusActive =
    statusNum === 1 ||
    statusNum === true ||
    String(statusNum).toLowerCase() === "active";

  const phoneRaw = row.phone != null ? String(row.phone).trim() : "";
  const emailRaw = row.email != null ? String(row.email).trim() : "";

  return (
    <div className={styles.viewModalInner}>
      <header className={styles.viewHeader}>
        <h2 className={styles.viewTitle}>Shop details</h2>
        <button
          type="button"
          className={styles.viewClose}
          onClick={onClose}
          aria-label="Close"
        >
          <FaCircleXmark size={22} />
        </button>
      </header>

      <div className={styles.viewHero}>
        <h3 className={styles.viewShopName}>{row.name ?? "—"}</h3>
        <div className={styles.viewBadges}>
          <span className={styles.badgeRating}>
            <FaStar size={14} aria-hidden />
            {ratingStr}
          </span>
          {statusNum != null && (
            <span
              className={`${styles.badgeStatus} ${
                statusActive ? styles.badgeStatusOn : styles.badgeStatusOff
              }`}
            >
              {statusActive
                ? "Active"
                : statusNum === 0 || statusNum === "0"
                ? "Inactive"
                : `Status ${String(statusNum)}`}
            </span>
          )}
        </div>
      </div>

      <div className={styles.viewCard}>
        <p className={styles.viewCardTitle}>Contact</p>
        <div className={styles.viewGrid2}>
          <div className={styles.viewField}>
            <span className={styles.viewLabel}>Email</span>
            <div className={styles.viewValue}>
              {emailRaw ? (
                <a className={styles.linkMuted} href={`mailto:${emailRaw}`}>
                  {emailRaw}
                </a>
              ) : (
                "—"
              )}
            </div>
          </div>
          <div className={styles.viewField}>
            <span className={styles.viewLabel}>Phone</span>
            <div className={styles.viewValue}>
              {phoneRaw ? (
                <a className={styles.linkMuted} href={`tel:${phoneRaw.replace(/\s/g, "")}`}>
                  {phoneRaw}
                </a>
              ) : (
                "—"
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.viewCard}>
        <p className={styles.viewCardTitle}>Location</p>
        <div className={styles.viewField}>
          <span className={styles.viewLabel}>Address</span>
          <div className={styles.viewValue}>{row.address?.trim() ? row.address : "—"}</div>
        </div>
        <div className={styles.viewField} style={{ marginTop: 14 }}>
          <span className={styles.viewLabel}>Coordinates</span>
          <div className={styles.viewValue}>
            {row.latitude != null || row.longitude != null
              ? `${row.latitude ?? "—"} · ${row.longitude ?? "—"}`
              : "—"}
          </div>
        </div>
      </div>

      <div className={styles.viewCard}>
        <p className={styles.viewCardTitle}>Social links</p>
        {socialEntries.length === 0 ? (
          <div className={styles.viewValue}>—</div>
        ) : (
          <div className={styles.socialChips}>
            {socialEntries.map(([key, val]) => {
              const v = String(val).trim();
              return (
                <div key={key} className={styles.socialChip}>
                  <span className={styles.socialKey}>{key}</span>
                  <span className={styles.socialVal}>
                    <a href={hrefToWeb(v)} target="_blank" rel="noopener noreferrer">
                      {v}
                    </a>
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className={styles.viewCard}>
        <p className={styles.viewCardTitle}>About</p>
        <div className={styles.viewValue}>
          {row.about?.trim() ? row.about : "—"}
        </div>
      </div>

      <div className={styles.viewCard}>
        <p className={styles.viewCardTitle}>Services</p>
        {servicesList == null ? (
          <div className={styles.viewValue}>—</div>
        ) : Array.isArray(servicesList) ? (
          <ul className={styles.servicesList}>
            {servicesList.filter(Boolean).map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        ) : (
          <div className={styles.viewValue}>{servicesList}</div>
        )}
      </div>

      <div className={styles.viewCard}>
        <p className={styles.viewCardTitle}>Opening hours</p>
        {hours.length === 0 ? (
          <div className={styles.viewValue}>—</div>
        ) : (
          <table className={styles.hoursTable}>
            <thead>
              <tr>
                <th>Day</th>
                <th>From</th>
                <th>To</th>
              </tr>
            </thead>
            <tbody>
              {hours.map((h, i) => (
                <tr key={i}>
                  <td>
                    <span className={styles.dayPill}>{getOpeningDayLabel(h)}</span>
                  </td>
                  <td>{formatTimeDisplay(h.time_from)}</td>
                  <td>{formatTimeDisplay(h.time_to)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function ShopFormFields({ form, setForm }) {
  const update = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const addHour = () => {
    setForm((prev) => ({
      ...prev,
      opening_hours: [
        ...prev.opening_hours,
        { day: "Mon", time_from: "", time_to: "" },
      ],
    }));
  };

  const updateHour = (index, field, value) => {
    setForm((prev) => {
      const next = [...prev.opening_hours];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, opening_hours: next };
    });
  };

  const removeHour = (index) => {
    setForm((prev) => ({
      ...prev,
      opening_hours: prev.opening_hours.filter((_, i) => i !== index),
    }));
  };

  return (
    <div className={styles.modalBody}>
      <Row>
        <Col md={12}>
          <p className="m-0">Name *</p>
          <Input
            classes="mt-2 mb-3"
            type="text"
            placeholder="Shop / location name"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
          />
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <p className="m-0">Email</p>
          <Input
            classes="mt-2 mb-3"
            type="email"
            placeholder="Email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
          />
        </Col>
        <Col md={6}>
          <p className="m-0">Phone</p>
          <Input
            classes="mt-2 mb-3"
            type="text"
            placeholder="Phone"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
          />
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <p className="m-0">Facebook</p>
          <Input
            classes="mt-2 mb-3"
            type="text"
            placeholder="Facebook URL"
            value={form.social_facebook}
            onChange={(e) => update("social_facebook", e.target.value)}
          />
        </Col>
        <Col md={6}>
          <p className="m-0">Instagram</p>
          <Input
            classes="mt-2 mb-3"
            type="text"
            placeholder="Instagram URL"
            value={form.social_instagram}
            onChange={(e) => update("social_instagram", e.target.value)}
          />
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <p className="m-0">TikTok</p>
          <Input
            classes="mt-2 mb-3"
            type="text"
            placeholder="TikTok URL"
            value={form.social_tiktok}
            onChange={(e) => update("social_tiktok", e.target.value)}
          />
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <p className="m-0">Latitude</p>
          <Input
            classes="mt-2 mb-3"
            type="text"
            placeholder="Latitude"
            value={form.latitude}
            onChange={(e) => update("latitude", e.target.value)}
          />
        </Col>
        <Col md={6}>
          <p className="m-0">Longitude</p>
          <Input
            classes="mt-2 mb-3"
            type="text"
            placeholder="Longitude"
            value={form.longitude}
            onChange={(e) => update("longitude", e.target.value)}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <p className="m-0">Address</p>
          <Input
            classes="mt-2 mb-3"
            type="text"
            placeholder="Address"
            value={form.address}
            onChange={(e) => update("address", e.target.value)}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <p className="m-0">About</p>
          <Input
            classes="mt-2 mb-3"
            type="text"
            as="textarea"
            rows={3}
            placeholder="About"
            value={form.about}
            onChange={(e) => update("about", e.target.value)}
          />
        </Col>
      </Row>

      <div className="d-flex justify-content-between align-items-center mb-2 mt-2">
        <p className="m-0 fw-medium">Opening hours</p>
        <button
          type="button"
          className={styles.removeHour}
          onClick={addHour}
        >
          + Add slot
        </button>
      </div>
      {form.opening_hours.map((slot, index) => (
        <div key={index} className={styles.hoursRow}>
          <div style={{ minWidth: "100px" }}>
            <p className="m-0 mb-1 small">Day</p>
            <Form.Select
              size="sm"
              value={slot.day}
              onChange={(e) => updateHour(index, "day", e.target.value)}
              className="text-dark"
            >
              {DAYS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </Form.Select>
          </div>
          <div style={{ minWidth: "110px" }}>
            <p className="m-0 mb-1 small">From</p>
            <Form.Control
              size="sm"
              type="time"
              value={slot.time_from}
              onChange={(e) => updateHour(index, "time_from", e.target.value)}
            />
          </div>
          <div style={{ minWidth: "110px" }}>
            <p className="m-0 mb-1 small">To</p>
            <Form.Control
              size="sm"
              type="time"
              value={slot.time_to}
              onChange={(e) => updateHour(index, "time_to", e.target.value)}
            />
          </div>
          <button
            type="button"
            className={styles.removeHour}
            onClick={() => removeHour(index)}
          >
            Remove
          </button>
        </div>
      ))}
    </div>
  );
}

const ShopManagement = () => {
  const {
    values: { list: rowData, loading, saving },
    functions: { handleCreateShop, handleUpdateShop, handleDeleteShop },
  } = useShopController();

  const [rowDataOptimistic, setRowDataOptimistic] = useState([]);
  const [indicationMessage, setIndicationMessage] = useState("loading...");
  const [editedData, setEditedData] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [viewRow, setViewRow] = useState(null);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (loading) {
      setIndicationMessage("loading...");
      return;
    }
    setIndicationMessage(rowData.length ? "" : "No shops found");
  }, [loading, rowData.length]);

  useEffect(() => {
    setRowDataOptimistic(rowData);
  }, [rowData]);

  const handleDeleteModalClose = () => {
    setDeleteModal(false);
    setEditedData(null);
  };

  const handleEditModalClose = () => {
    setEditModal(false);
    setEditedData(null);
    setForm(emptyForm());
  };

  const handleAddModalOpen = () => {
    setForm(emptyForm());
    setAddModal(true);
  };

  const handleAddModalClose = () => {
    setAddModal(false);
    setForm(emptyForm());
  };

  const onConfirmDelete = async (id) => {
    try {
      await handleDeleteShop(id);
      handleDeleteModalClose();
    } catch {
      /* Toast in controller */
    }
  };

  const openEdit = (row) => {
    setEditedData(row);
    setForm(rowToForm(row));
    setEditModal(true);
  };

  const validateAndPayload = () => {
    if (!form.name.trim()) {
      Toast("Please enter a name", "error");
      return null;
    }
    return formToPayload(form);
  };

  const handleSaveAdd = async () => {
    const payload = validateAndPayload();
    if (!payload) return;
    try {
      await handleCreateShop(payload);
      handleAddModalClose();
    } catch {
      /* Toast in controller */
    }
  };

  const handleSaveEdit = async () => {
    if (!editedData?.id) return;
    const payload = validateAndPayload();
    if (!payload) return;
    try {
      await handleUpdateShop(editedData.id, payload);
      handleEditModalClose();
    } catch {
      /* Toast in controller */
    }
  };

  function indication() {
    return indicationMessage;
  }

  const displayRows = rowDataOptimistic;

  const columns = [
    {
      dataField: "name",
      text: "Name",
      sort: true,
      headerStyle: { width: "12%", minWidth: "100px" },
      style: { width: "12%", minWidth: "100px" },
      formatter: (cell, row) => row.name ?? cell ?? "-",
    },
    {
      dataField: "email",
      text: "Email",
      sort: true,
      headerStyle: { width: "22%", minWidth: "160px" },
      style: { width: "22%", minWidth: "160px", overflow: "hidden" },
      formatter: (c, row) => (
        <span className={styles.emailCell} title={String(row.email ?? c ?? "")}>
          {row.email ?? c ?? "-"}
        </span>
      ),
    },
    {
      dataField: "phone",
      text: "Phone",
      sort: true,
      headerStyle: { width: "14%", minWidth: "140px" },
      style: {
        width: "14%",
        minWidth: "140px",
        overflow: "hidden",
      },
      formatter: (c, row) => {
        const raw = row.phone ?? c ?? "";
        const normalized = String(raw).replace(/\s+/g, " ").trim();
        return (
          <span className={styles.phoneCell} title={normalized || undefined}>
            {normalized || "-"}
          </span>
        );
      },
    },
    {
      dataField: "address",
      text: "Address",
      sort: true,
      headerStyle: { width: "18%", minWidth: "120px" },
      style: { width: "18%", minWidth: "120px", overflow: "hidden" },
      formatter: (c, row) => {
        const full = row.address ?? c ?? "";
        const short =
          full.length > 42 ? `${String(full).slice(0, 42)}…` : full || "-";
        return (
          <span className={styles.addressPreview} title={full || undefined}>
            {short}
          </span>
        );
      },
    },
    {
      dataField: "status",
      text: "Status",
      sort: true,
      headerStyle: { width: "8%", minWidth: "64px" },
      style: { width: "8%", minWidth: "64px" },
      formatter: (c, row) =>
        row.status != null ? String(row.status) : c != null ? String(c) : "-",
    },
    {
      dataField: "actions",
      text: "Actions",
      headerAlign: "center",
      align: "center",
      headerStyle: { width: "26%", minWidth: "280px" },
      style: { width: "26%", minWidth: "280px", overflow: "visible" },
      formatter: (_cell, row) => {
        if (row.id == null) return null;
        return (
          <div className={styles.actionsRow}>
            <div
              className={`${styles.viewBtn} d-flex justify-content-center align-items-center rounded-3 px-2 py-2`}
              role="button"
              onClick={() => setViewRow(row)}
            >
              <span className="align-items-center d-flex justify-content-center pe-1">
                <FaEye />
              </span>
              <span>View</span>
            </div>
            <div
              className={`${styles.btnBg} d-flex justify-content-center align-items-center rounded-3 px-2 py-2`}
              role="button"
              onClick={() => openEdit(row)}
            >
              <span className="align-items-center d-flex justify-content-center pe-1">
                <FaUserEdit />
              </span>
              <span>Edit</span>
            </div>
            <div
              className="d-flex justify-content-center align-items-center bg-danger rounded-3 px-2 py-2"
              role="button"
              onClick={() => {
                setEditedData({
                  ...row,
                  name: row.name || "this shop",
                });
                setDeleteModal(true);
              }}
            >
              <span className="d-flex align-items-center pe-1">
                <MdDeleteForever />
              </span>
              <span>Delete</span>
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <div className={styles.MainContainer}>
      {deleteModal ? (
        <DeleteModal
          handleDeleteModalClose={handleDeleteModalClose}
          data={editedData}
          onDelete={onConfirmDelete}
        />
      ) : null}

      {viewRow ? (
        <ModalContainer handleClose={() => setViewRow(null)}>
          <div
            className={`${modalStyles.modalContainer} ${styles.shopViewContainer}`}
          >
            <ShopViewBody row={viewRow} onClose={() => setViewRow(null)} />
          </div>
        </ModalContainer>
      ) : null}

      {editModal ? (
        <ModalContainer handleClose={handleEditModalClose}>
          <div className={modalStyles.modalContainer}>
            <div className="d-flex align-items-center w-100 justify-content-between mb-3">
              <p className="fs-4 fw-medium m-0">Edit Shop</p>
              <span className="bg-dark bg-opacity-75 d-flex p-3 rounded-5">
                <FaCircleXmark
                  role="button"
                  onClick={handleEditModalClose}
                  style={{ cursor: "pointer" }}
                />
              </span>
            </div>
            <ShopFormFields form={form} setForm={setForm} />
            <Row className="mt-3">
              <Col md={12}>
                <Button
                  text={saving ? "Saving..." : "Update Shop"}
                  onClick={handleSaveEdit}
                  disabled={saving}
                />
              </Col>
            </Row>
          </div>
        </ModalContainer>
      ) : null}

      {addModal ? (
        <ModalContainer handleClose={handleAddModalClose}>
          <div className={modalStyles.modalContainer}>
            <div className="d-flex align-items-center w-100 justify-content-between mb-3">
              <p className="fs-4 fw-medium m-0">Add Shop</p>
              <span className="bg-dark bg-opacity-75 d-flex p-3 rounded-5">
                <FaCircleXmark
                  role="button"
                  onClick={handleAddModalClose}
                  style={{ cursor: "pointer" }}
                />
              </span>
            </div>
            <ShopFormFields form={form} setForm={setForm} />
            <Row className="mt-3">
              <Col md={12}>
                <Button
                  text={saving ? "Saving..." : "Save Shop"}
                  onClick={handleSaveAdd}
                  disabled={saving}
                />
              </Col>
            </Row>
          </div>
        </ModalContainer>
      ) : null}

      <div className={styles.headerContainer}>
        <div className="w-100 flex-between">
          <SubTitle text="Shop Management" />
          <AddButton text="Add Shop" onClick={handleAddModalOpen} />
        </div>
      </div>

      <div className={`${styles.mainTable} ${styles.shopTableWrap}`}>
        <Table data={displayRows} columns={columns} indication={indication} />
      </div>
    </div>
  );
};

export default ShopManagement;
